import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { ImageIcon, Loader2 } from 'lucide-react';

/**
 * Renders an image that lives in secure object storage. Because <img src> cannot
 * send an Authorization header, we fetch the bytes as a blob (Bearer token via the
 * api interceptor) and render an object URL. Cleans up the URL on unmount.
 *
 * Props:
 *  - path: storage path returned by POST /uploads/image (e.g. "shabzcloset/uploads/..")
 *  - alt, className: passed to the <img>
 */
export function AuthImage({ path, alt = 'photo', className = '', onClick }) {
  const [url, setUrl] = useState('');
  const [state, setState] = useState('loading'); // loading | ok | error
  const urlRef = useRef('');

  useEffect(() => {
    let cancelled = false;
    if (!path) { setState('error'); return; }
    setState('loading');
    (async () => {
      try {
        const r = await api.get(`/files/${path}`, { responseType: 'blob' });
        if (cancelled) return;
        const objUrl = URL.createObjectURL(r.data);
        urlRef.current = objUrl;
        setUrl(objUrl);
        setState('ok');
      } catch (e) {
        if (!cancelled) setState('error');
      }
    })();
    return () => {
      cancelled = true;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = ''; }
    };
  }, [path]);

  if (state === 'loading') {
    return (
      <div className={`flex items-center justify-center bg-secondary/50 ${className}`} data-testid="authimage-loading">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className={`flex items-center justify-center bg-secondary/50 text-muted-foreground ${className}`} data-testid="authimage-error">
        <ImageIcon className="h-4 w-4" />
      </div>
    );
  }
  return <img src={url} alt={alt} className={className} onClick={onClick} data-testid="authimage-img" />;
}
